// @flow
import React, { useEffect, useRef } from 'react';
import { t } from '@lingui/macro';
import { I18n } from '@lingui/react';
import styled, { type StyledComponent } from 'styled-components';

import { DSTokenMap } from '@rover/kibble/styles';
import Input, { type Props as InputProps } from '@rover/react-lib/components/formFields/Input';
import type { Untranslated } from '@rover/shared/js/types';

import { initIntlPhoneInput } from './IntlPhoneInputUtils';

type IntlPhoneInputStylingWrapperProps = {
  styleAsError?: boolean,
};

export const IntlPhoneInputStylingWrapper: StyledComponent<
  IntlPhoneInputStylingWrapperProps,
  {},
  any
> = styled.div`
  .iti {
    width: 100%;
  }
  input {
    border: 1px solid
      ${(props) =>
        props.styleAsError ? DSTokenMap.BORDER_COLOR_ERROR : DSTokenMap.BORDER_COLOR_PRIMARY};
  }
`;

export type Props = {
  ...InputProps,
  placeholder: Untranslated,
  label: Untranslated,
  onChange: (string, boolean) => void,
  countryCode: string,
  locale: string,
  supportedCountryCodes: Array<string>,
  validationError?: ?Untranslated,
};

const IntlPhoneInput = ({
  placeholder,
  label,
  onChange,
  countryCode,
  locale,
  supportedCountryCodes,
  validationError,
  ...other
}: Props) => {
  const inputRef = useRef();
  const intlPhonePluginRef = useRef();

  useEffect(() => {
    const init = async () => {
      if (inputRef.current) {
        intlPhonePluginRef.current = await initIntlPhoneInput(
          inputRef.current,
          onChange,
          countryCode,
          locale,
          supportedCountryCodes
        );
      } else {
        throw new Error('inputRef undefined during initIntlPhoneInput()');
      }
    };

    init();
    return () => intlPhonePluginRef.current && intlPhonePluginRef.current.cleanup();
  }, []);

  useEffect(() => {
    if (intlPhonePluginRef.current) {
      intlPhonePluginRef.current.pluginInstance.setCountry(countryCode);
    }
  }, [countryCode]);

  return (
    <I18n>
      {({ i18n }) => (
        <IntlPhoneInputStylingWrapper styleAsError={!!validationError}>
          <Input
            {...other}
            sensitive
            type="tel"
            label={i18n._(label)}
            placeholder={i18n._(placeholder)}
            inputRef={(node: HTMLInputElement) => {
              inputRef.current = node;
            }}
            validationError={validationError}
          />
        </IntlPhoneInputStylingWrapper>
      )}
    </I18n>
  );
};

IntlPhoneInput.defaultProps = {
  label: t`Phone Number`,
  placeholder: t`Phone Number`,
  countryCode: 'us',
  locale: 'en-us',
  supportedCountryCodes: ['us'],
};

export default IntlPhoneInput;



// WEBPACK FOOTER //
// ../react-lib/src/components/formFields/IntlPhoneInput/IntlPhoneInput.jsx